import { useCallback, useState } from 'react';

import type { BackgroundMode } from '../useModelViewerBackground';

interface UseModelViewerBackgroundControlsOptions {
  backgroundMode: BackgroundMode;
  customColor: string;
  setCustomColor: (hex: string) => void;
  setPresetBackground: (mode: 'default' | 'black' | 'white' | 'gray') => void;
}

function useModelViewerBackgroundControls({
  backgroundMode,
  customColor,
  setCustomColor,
  setPresetBackground
}: UseModelViewerBackgroundControlsOptions) {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorPickerPrevMode, setColorPickerPrevMode] =
    useState<BackgroundMode>('default');
  const [colorPickerPrevColor, setColorPickerPrevColor] = useState('#ffffff');

  const [imageModalOpen, setImageModalOpen] = useState(false);

  const handleOpenColorPicker = useCallback(() => {
    setColorPickerPrevMode(backgroundMode);
    setColorPickerPrevColor(customColor);
    setColorPickerOpen(true);
  }, [backgroundMode, customColor]);

  const handleConfirmColorPicker = useCallback(
    (hex: string) => {
      setCustomColor(hex);
      setColorPickerOpen(false);
    },
    [setCustomColor]
  );

  const handleCancelColorPicker = useCallback(() => {
    if (colorPickerPrevMode !== 'custom') {
      setPresetBackground(
        colorPickerPrevMode as 'default' | 'black' | 'white' | 'gray'
      );
    } else {
      setCustomColor(colorPickerPrevColor);
    }
    setColorPickerOpen(false);
  }, [
    colorPickerPrevMode,
    colorPickerPrevColor,
    setCustomColor,
    setPresetBackground
  ]);

  const handleOpenImageModal = useCallback(() => {
    setImageModalOpen(true);
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setImageModalOpen(false);
  }, []);

  return {
    colorPickerOpen,
    colorPickerPrevColor,
    imageModalOpen,
    handleOpenColorPicker,
    handleConfirmColorPicker,
    handleCancelColorPicker,
    handleOpenImageModal,
    handleCloseImageModal
  };
}

export default useModelViewerBackgroundControls;
